import { useCallback, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  formatHours,
  listEmployeeTimeAccounts,
  type EmployeeTimeAccount,
} from "@/lib/employeeTimes";
import {
  ACCOUNTS_COLUMNS,
  getAccountsColumnDefaultWidth,
  getAccountsColumnLabel,
  renderAccountsCell,
} from "@/pages/admin/zeiterfassung/columns";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";
import { useZeiterfassungColumnWidths } from "@/pages/admin/zeiterfassung/useZeiterfassungColumnWidths";
import {
  ZeiterfassungDataTable,
  type ZeiterfassungTableColumn,
} from "@/pages/admin/zeiterfassung/ZeiterfassungDataTable";

interface AccountsPanelProps {
  refreshKey: number;
}

const MONTH_NAMES = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export function AccountsPanel({ refreshKey }: AccountsPanelProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [accounts, setAccounts] = useState<EmployeeTimeAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { widths, onColumnWidthChange } = useZeiterfassungColumnWidths("accounts");

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const rows = await listEmployeeTimeAccounts({ year, month });
      setAccounts(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Stundenkonten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useRefreshKeyLoad(load, refreshKey);

  const monthTotal = accounts.reduce((sum, a) => sum + Number(a.month_hours || 0), 0);
  const allTimeTotal = accounts.reduce((sum, a) => sum + Number(a.total_hours || 0), 0);

  const columns = useMemo(
    (): ZeiterfassungTableColumn<EmployeeTimeAccount>[] =>
      ACCOUNTS_COLUMNS.map((key) => ({
        key,
        label: getAccountsColumnLabel(key),
        defaultWidth: getAccountsColumnDefaultWidth(key),
        mono: key === "login" || key === "month_hours" || key === "total_hours",
        align:
          key === "month_hours" || key === "total_hours" || key === "entry_count" || key === "project_count"
            ? "right"
            : "left",
        className:
          key === "month_hours" || key === "total_hours" ? "font-semibold" : undefined,
        sortValue: (account) => renderAccountsCell(account, key),
        render: (account) => {
          const value = renderAccountsCell(account, key);
          return value || <span className="text-label-tertiary">—</span>;
        },
      })),
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios">
        <label className="space-y-1">
          <span className="eh-label">Jahr</span>
          <select
            className="eh-input"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="eh-label">Monat</span>
          <select
            className="eh-input"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={name} value={idx + 1}>{name}</option>
            ))}
          </select>
        </label>
        <Button variant="secondary" onClick={() => void load()}>
          Aktualisieren
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <div className="text-xs text-label-secondary">Monatssumme (alle)</div>
          <div className="mt-1 text-2xl font-semibold text-label">{formatHours(monthTotal)}</div>
          <div className="mt-1 text-sm text-label-secondary">
            {MONTH_NAMES[month - 1]} {year}
          </div>
        </div>
        <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <div className="text-xs text-label-secondary">Gesamtsumme (alle Zeiten)</div>
          <div className="mt-1 text-2xl font-semibold text-label">{formatHours(allTimeTotal)}</div>
        </div>
      </div>

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <ZeiterfassungDataTable
          rows={accounts}
          columns={columns}
          rowKey={(row) => row.user_login}
          widths={widths}
          onColumnWidthChange={onColumnWidthChange}
          emptyText="Noch keine Stunden erfasst."
          renderMobileCard={(account) => (
            <div className="rounded-ios border border-separator bg-card px-4 py-3 shadow-ios">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-label">
                    {account.user_display || account.user_login}
                  </div>
                  <div className="text-xs text-label-secondary">{account.user_login}</div>
                </div>
                <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
                  {account.project_count} Projekte
                </span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                <dt className="text-label-tertiary">Monat</dt>
                <dd className="text-right font-semibold tabular-nums text-label">
                  {formatHours(account.month_hours)}
                </dd>
                <dt className="text-label-tertiary">Gesamt</dt>
                <dd className="text-right font-semibold tabular-nums text-label">
                  {formatHours(account.total_hours)}
                </dd>
                <dt className="text-label-tertiary">Einträge</dt>
                <dd className="text-right text-label">{account.entry_count}</dd>
              </dl>
            </div>
          )}
        />
      )}
    </div>
  );
}
